import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, type SxProps, type Theme, Typography, useMediaQuery } from "@mui/material";
import { type FC, useId } from "react";
import { CONTROL_HEIGHT, FONT_SIZE, ICON_SIZE, LETTER_SPACING, MOTION, NAME_DISCLOSURE } from "../../../theme/tokens";
import MetaChip, { type MetaChipTone } from "../Badges/MetaChip";
import SystemButton from "../SystemButton/SystemButton";

/** One named thing — usually a person. Pass `id` whenever there is one: two employees can share a name. */
export type NameDisclosureItem = {
	id?: string | number;
	/** Already-translated display label. */
	label: string;
	/** Optional short annotation rendered as a `MetaChip` after the name. Already translated. */
	tag?: string;
	/** Semantic tone for `tag`. Default `neutral`. */
	tagTone?: MetaChipTone;
};

/** A run of names. One unlabeled section is a flat list; label them to split by reason. */
export type NameDisclosureSection = {
	id: string;
	/** Already-translated sub-heading, rendered with the section's count. Omit for a flat list. */
	label?: string;
	items: NameDisclosureItem[];
};

type Props = {
	/** Controlled: the caller owns which lists are open (e.g. "short lists start revealed"). */
	open: boolean;
	onToggle: () => void;
	/** Already-translated toggle labels, e.g. "Show 188 employees" / "Hide employees". */
	showLabel: string;
	hideLabel: string;
	/** Sections with no items are dropped. */
	sections: NameDisclosureSection[];
	/** `list` (default) = one bulleted column; `columns` = a multi-column grid for hundreds of short names. */
	layout?: "list" | "columns";
	/** Revealed names scroll past this height (px). Default `NAME_DISCLOSURE.LIST_MAX_HEIGHT`. */
	maxHeight?: number;
	/** A compact toggle, for a list sitting inside a card that already has its own header. */
	dense?: boolean;
	/** Test id of the root. */
	dataTestId?: string;
	toggleTestId?: string;
	listTestId?: string;
	/** Test id per section, from its id. */
	sectionTestId?: (sectionId: string) => string;
	/** Test id per row — a function so a caller can use one shared id or a per-item one. */
	itemTestId?: (item: NameDisclosureItem) => string;
	sx?: SxProps<Theme>;
};

/**
 * A list of names behind a "Show N / Hide" disclosure: the chevron toggle, the reveal, and a list that
 * scrolls past a height floor instead of pushing the rest of a dialog away. The shared body of
 * `ConflictBlockedDialog`'s blocker groups and `NamedGroupCard`'s groups — anywhere a count is the
 * headline and the names behind it are a reference the reader opts into.
 *
 * Open state is controlled, so each caller keeps its own "which lists start revealed" rule. The toggle
 * is a `SystemButton` wired with `aria-expanded`/`aria-controls`; the chevron turn and the reveal drop
 * their motion under `prefers-reduced-motion`. Names ellipsize rather than wrap, with the full name on hover.
 */
export const NameDisclosureList: FC<Props> = ({
	open,
	onToggle,
	showLabel,
	hideLabel,
	sections,
	layout = "list",
	maxHeight = NAME_DISCLOSURE.LIST_MAX_HEIGHT,
	dense = false,
	dataTestId,
	toggleTestId,
	listTestId,
	sectionTestId,
	itemTestId,
	sx,
}) => {
	const listId = useId();
	const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
	const visibleSections = sections.filter((section) => section.items.length > 0);
	const revealMs = prefersReducedMotion ? 0 : MOTION.DURATION.ENTRANCE_MD;

	return (
		<Box sx={sx} data-testid={dataTestId}>
			<SystemButton
				buttonVariant="borderless"
				size="small"
				// `&&` so the compact size wins over SystemButton's own size class rather than tying with it.
				sx={dense ? { "&&": { fontSize: FONT_SIZE.SECONDARY, height: CONTROL_HEIGHT.COMPACT, minHeight: CONTROL_HEIGHT.COMPACT } } : undefined}
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={listId}
				data-testid={toggleTestId}
				leadingIcon={
					<ExpandMore
						sx={{
							fontSize: ICON_SIZE.INLINE,
							transform: open ? "rotate(180deg)" : "rotate(0deg)",
							transition: prefersReducedMotion ? "none" : `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
						}}
					/>
				}
			>
				{open ? hideLabel : showLabel}
			</SystemButton>
			<Collapse in={open} timeout={revealMs} unmountOnExit>
				<Box id={listId} data-testid={listTestId} sx={{ mt: 0.5, maxHeight, overflowY: "auto" }}>
					{visibleSections.map((section) => (
						<NameSection key={section.id} section={section} layout={layout} sectionTestId={sectionTestId} itemTestId={itemTestId} />
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

type SectionProps = Pick<Props, "layout" | "sectionTestId" | "itemTestId"> & { section: NameDisclosureSection };

const LIST_SX: SxProps<Theme> = { m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 };
const COLUMNS_SX: SxProps<Theme> = {
	m: 0,
	px: 0.5,
	listStyle: "none",
	display: "grid",
	gridTemplateColumns: `repeat(auto-fill, minmax(${NAME_DISCLOSURE.COLUMN_MIN_WIDTH}px, 1fr))`,
	columnGap: 1.5,
	rowGap: 0.5,
};

const NameSection: FC<SectionProps> = ({ section, layout, sectionTestId, itemTestId }) => (
	<Box data-testid={sectionTestId?.(section.id)} sx={{ "& + &": { pt: 1 } }}>
		{section.label && (
			<Typography
				component="h4"
				sx={{
					px: 0.5,
					pb: 0.5,
					fontSize: FONT_SIZE.LABEL_XS,
					fontWeight: 600,
					letterSpacing: LETTER_SPACING.LABEL,
					textTransform: "uppercase",
					color: "text.secondary",
				}}
			>
				{`${section.label} · ${section.items.length.toLocaleString()}`}
			</Typography>
		)}
		<Box component="ul" sx={layout === "columns" ? COLUMNS_SX : LIST_SX}>
			{section.items.map((item, index) => (
				<Box
					component="li"
					key={item.id ?? `${section.id}-${index}`}
					data-testid={itemTestId?.(item)}
					sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
				>
					<Typography variant="body2" title={item.label} sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{item.label}
					</Typography>
					{/* DEFAULT: an untoned tag is plain annotation, so it takes the quiet neutral chip. */}
					{item.tag && <MetaChip label={item.tag} tone={item.tagTone ?? "neutral"} size="small" />}
				</Box>
			))}
		</Box>
	</Box>
);

export default NameDisclosureList;
